import React, { useEffect, useRef, useState } from "react"
import { CLOSED_STATES, COUNTRIES, OPEN_STATES, SPORTS, nextSeason, previousSeason, seasonLabel } from "./plannerQuery"
import { NONE, facetCounts } from "./plannerItems"
import { categoryLabel, organizerLabel, sportLabel, stateLabel } from "./plannerLabels"

// A filter that picks several choices, each with the number of tournaments it keeps. `changed` says whether the choice differs from
// the default (nothing chosen, or the states read by default).
function FilterMenu({ label, options, chosen, changed = chosen.length > 0, open, onToggle, onChange }) {
  const toggle = value => onChange(chosen.includes(value) ? chosen.filter(item => item !== value) : [...chosen, value])
  return <div className="ep-filter">
    <button type="button" className={"ep-chip" + (changed ? " is-on" : "")} aria-expanded={open} onClick={onToggle}>
      {changed ? `${label}: ${chosen.length}` : label}<i className="bx bx-chevron-down" aria-hidden="true" />
    </button>
    {open && <div className="ep-filter-panel" role="group" aria-label={label}>
      {options.length ? options.map(option => <label key={option.value} className="ep-filter-option">
        <input type="checkbox" checked={chosen.includes(option.value)} onChange={() => toggle(option.value)} />
        <span>{option.count === undefined ? option.label : `${option.label} (${option.count})`}</span>
      </label>) : <span className="ep-meta">Nothing to choose in this view.</span>}
      <div className="ep-filter-actions">
        <button type="button" className="btn btn-link btn-sm" onClick={() => onChange([])} disabled={!chosen.length}>Clear</button>
        <button type="button" className="btn btn-primary btn-sm" onClick={onToggle}>Done</button>
      </div>
    </div>}
  </div>
}

const byLabel = (a, b) => (a.value === NONE) - (b.value === NONE) || a.label.localeCompare(b.label)
// A facet's choices: those the view holds, with their counts, and those chosen that it no longer holds.
const facetOptions = (items, query, today, facet, labelOf, order = byLabel) => {
  const counts = facetCounts(items, query, today, facet)
  const values = [...new Set([...counts.keys(), ...query[facet]])]
  return values.map(value => ({ value, label: labelOf(value, counts.get(value)?.item), count: counts.get(value)?.count || 0 })).sort(order)
}

// The view's filters (story 4; B3 pinned decision Q3): one country at a time, the season, the sports, the circuit (Sportya; Tenis10
// is not selectable until its release), then category, organizer, county, region and state, the published events, and only the
// proposals with an open warning (ticket 13 Q11).
export default function PlannerFilters({ query, items, today, settings, onChange, onReset }) {
  const [open, setOpen] = useState(null)
  const section = useRef(null)
  // An open choice closes on Esc and on a click outside the filters.
  useEffect(() => {
    const onKey = event => event.key === "Escape" && setOpen(null)
    const onClick = event => !section.current?.contains(event.target) && setOpen(null)
    document.addEventListener("keydown", onKey)
    document.addEventListener("mousedown", onClick)
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick) }
  }, [])
  const menu = (key, label, options, changed) => <FilterMenu key={key} label={label} options={options} chosen={query[key]} changed={changed}
    open={open === key} onToggle={() => setOpen(open === key ? null : key)} onChange={values => onChange({ [key]: values })} />
  const categoryOrder = (settings.categories || []).map(({ key }) => key)
  const categories = facetOptions(items, query, today, "categories", value => categoryLabel(settings, value),
    (a, b) => categoryOrder.indexOf(a.value) - categoryOrder.indexOf(b.value))
  // A choice the view no longer holds is still named, so it can be cleared.
  const organizers = facetOptions(items, query, today, "organizers", (value, item) => (value === NONE ? organizerLabel(null)
    : item ? organizerLabel(item.organizer) : "An organizer not in this view"))
  const counties = facetOptions(items, query, today, "counties", (value, item) => (value === NONE ? "No county (Fără județ)" : item?.county || value))
  const regions = facetOptions(items, query, today, "regions", value => (value === NONE ? "No region" : value))
  const states = [...OPEN_STATES, ...CLOSED_STATES].map(state => ({ value: state, label: stateLabel(state) }))
  const toggleSport = sport => {
    const sports = query.sports.includes(sport) ? query.sports.filter(item => item !== sport) : [...query.sports, sport]
    if (sports.length) onChange({ sports: SPORTS.filter(item => sports.includes(item)) })
  }
  const statesChanged = query.states.join(",") !== OPEN_STATES.join(",")
  const filtered = ["categories", "organizers", "counties", "regions"].some(key => query[key].length) || !query.published || statesChanged
    || query.warnings || Boolean(query.queue)

  return <section className="ep-card ep-filters" aria-label="Filters" ref={section}>
    <div className="ep-filter-row">
      <span className="ep-label">Country</span>
      <span className="ep-segment">{COUNTRIES.map(country => <button type="button" key={country} aria-pressed={query.country === country}
        className={"ep-chip" + (query.country === country ? " is-on" : "")} onClick={() => onChange({ country })}>{country}</button>)}</span>
      <span className="ep-label">Season</span>
      <span className="ep-season">
        <button type="button" className="ep-icon-button" aria-label="Previous season" onClick={() => onChange({ season: previousSeason(query.season) })}>
          <i className="bx bx-chevron-left" aria-hidden="true" /></button>
        <b>{seasonLabel(query.season)}</b>
        <button type="button" className="ep-icon-button" aria-label="Next season" onClick={() => onChange({ season: nextSeason(query.season) })}>
          <i className="bx bx-chevron-right" aria-hidden="true" /></button>
      </span>
      <span className="ep-label">Sport</span>
      <span className="ep-segment">{SPORTS.map(sport => <button type="button" key={sport} aria-pressed={query.sports.includes(sport)}
        className={"ep-chip" + (query.sports.includes(sport) ? " is-on" : "")} onClick={() => toggleSport(sport)}>{sportLabel(sport)}</button>)}</span>
      <span className="ep-label">Circuit</span>
      <span className="ep-segment">
        <button type="button" className="ep-chip is-on" aria-pressed="true" disabled>Sportya</button>
        <button type="button" className="ep-chip" aria-pressed="false" disabled title="Available after the Tenis10 release">Tenis10</button>
      </span>
    </div>
    <div className="ep-filter-row">
      {menu("categories", "Category", categories)}
      {menu("organizers", "Organizer", organizers)}
      {menu("counties", "County", counties)}
      {menu("regions", "Region", regions)}
      {menu("states", "State", states, statesChanged)}
      <label className="ep-switch">
        <input type="checkbox" checked={query.published} onChange={event => onChange({ published: event.target.checked })} />
        <span>Show published events</span>
      </label>
      <label className="ep-switch">
        <input type="checkbox" checked={query.warnings} onChange={event => onChange({ warnings: event.target.checked })} />
        <span>Only with warnings</span>
      </label>
      {filtered && <button type="button" className="btn btn-link btn-sm" onClick={onReset}>Reset filters</button>}
    </div>
  </section>
}
